import React, { useState } from 'react';
import { Lightbulb, X, ChevronDown, ChevronUp } from 'lucide-react';
import { SmallText } from './BrandTypography';

/**
 * MicroInsight - ADHD Brain Science Educational Component
 * 
 * Displays short, validating, non-clinical insights about WHY emotional
 * dysregulation happens in ADHD brains. These insights are:
 * - Always OPTIONAL and skippable
 * - Brief (1-2 sentences max)
 * - Appear AFTER a step is completed, never before
 * - Use soft, validating, non-clinical language
 * - Framed as "common experiences" or "research suggests"
 */

// General ADHD brain science insights (rotated randomly)
export const GENERAL_INSIGHTS = [
  "ADHD brains often feel emotions more intensely because the amygdala reacts more strongly to stress and stimulation.",
  "The amygdala helps process fear, anxiety, and big feelings — and in ADHD, it can activate faster and harder.",
  "The frontal cortex, which helps you pause and choose your response, can be less active in ADHD. That's why reactions can feel sudden.",
  "Your brain isn't failing you. ADHD affects the systems that regulate emotions, impulses, and reactions.",
  "Big feelings don't mean you're dramatic. They're part of how your brain is wired.",
  "Emotional intensity in ADHD is neurological, not a character flaw. Your feelings are valid.",
  "Research suggests ADHD brains have different dopamine pathways, which can affect how emotions are regulated.",
  "It's common for people with ADHD to experience emotions at higher intensity. This isn't weakness—it's wiring."
];

// Overwhelm-specific insights (by type)
export const OVERWHELM_INSIGHTS = {
  frozen: [
    "Frozen states can happen when the amygdala signals overwhelm faster than your frontal cortex can respond.",
    "Feeling stuck is your nervous system's way of protecting you when things feel like too much.",
    "The 'freeze' response is ancient and automatic. Your brain is trying to keep you safe.",
    "When we freeze, the thinking brain goes offline temporarily. Grounding helps bring it back."
  ],
  scattered: [
    "Scattered feelings often appear when your attention system is overloaded and trying to manage too much at once.",
    "A racing mind is common in ADHD because the brain's 'filter' for prioritizing information works differently.",
    "Feeling scattered isn't a failure of willpower. It's how the ADHD brain responds to overwhelm.",
    "When attention scatters, it's often the brain's attempt to find something that feels manageable."
  ],
  flooded: [
    "Flooded emotions happen when the amygdala fires intensely and the frontal cortex can't slow things down quickly.",
    "Emotional flooding is common in ADHD because the brain's regulation systems work differently under stress.",
    "Big waves of emotion aren't overreaction—they're how your nervous system processes intense input.",
    "When emotions flood, it's the amygdala doing its job. Regulation helps calm the storm."
  ]
};

// Grounding and reset insights
export const GROUNDING_INSIGHTS = [
  "Quick grounding helps because it gives your frontal cortex a moment to come back online.",
  "Sensory resets calm the amygdala, which is why they work so well for ADHD brains.",
  "Physical sensations help 'wake up' the thinking brain when emotions have taken over.",
  "Grounding works by interrupting the stress response and giving your brain something neutral to focus on."
];

// Self-talk reframe insights
export const REFRAME_INSIGHTS = [
  "Your brain may jump to intense emotions because the amygdala activates quickly in ADHD.",
  "It's harder for the ADHD brain to 'filter' emotional reactions because the frontal cortex is less active during stress.",
  "You're not overreacting on purpose. Your brain is processing emotions differently.",
  "Self-criticism often feels automatic because ADHD brains can get stuck in negative thought loops.",
  "Reframing works by engaging the frontal cortex, helping it regulate the amygdala's response."
];

// Co-regulation insights
export const COREGULATION_INSIGHTS = {
  solo: [
    "It's harder to self-soothe when the amygdala is activated and the frontal cortex is quieter. That's why these steps help.",
    "Self-regulation takes more effort for ADHD brains because the systems involved work differently.",
    "Being patient with yourself matters. ADHD brains need more time to shift out of intense emotional states.",
    "Connection with yourself is still co-regulation. Your calm voice can help soothe your own nervous system."
  ],
  partner: [
    "ADHD brains can react strongly in relationships because emotional signals fire quickly. Slowing down together helps both nervous systems.",
    "Co-regulation with a partner works because another person's calm can help regulate your own nervous system.",
    "Conflict feels more intense with ADHD because the brain's threat detection is more sensitive.",
    "Repair is always possible. ADHD brains may struggle in the moment but are capable of deep connection."
  ]
};

// ============================================
// HOW TO USE THESE TOOLS FOR ADHD - Educational Module
// ============================================

// 1. INTEROCEPTION SUPPORT - Body sensation prompts
export const INTEROCEPTION_INSIGHTS = [
  "Start with the body. What sensation do you notice first — tight jaw, heavy chest, buzzing hands?",
  "ADHD can make it harder to sense internal states. Begin with one physical cue.",
  "Before naming the emotion, notice: where does your body feel tight, warm, or buzzing?",
  "Your body often knows before your mind does. What's one sensation you notice right now?",
  "Tuning into body sensations helps bridge the gap between feeling and understanding.",
  "It's okay if the feeling is vague. Even noticing 'something in my chest' is a start.",
  "ADHD brains can struggle with interoception. Starting with the body makes emotions easier to find."
];

// 2. EMOTION SENSATION WHEEL - Mapping sensations to emotions
export const EMOTION_WHEEL_INSIGHTS = [
  "If you're not sure what you're feeling, start with the sensation. Tight jaw often links to anxiety or anger.",
  "Heavy chest might mean sadness or grief. Buzzing hands could be excitement or anxiety.",
  "Use sensation as a clue: shallow breathing often connects to fear or stress.",
  "Fluttery stomach? That could be nervousness, excitement, or anticipation.",
  "Tension in shoulders often points to stress, frustration, or carrying too much.",
  "Heaviness in limbs can signal exhaustion, depression, or overwhelm.",
  "Body sensations are messengers. They're trying to tell you something about how you feel."
];

// 3. NAME IT TO TAME IT - Labeling insights
export const LABELING_INSIGHTS = [
  "Naming the emotion helps your brain process it. Even a simple word like 'hard' counts.",
  "Try putting a word to this feeling — anxious, overwhelmed, frustrated, sad, stuck.",
  "Labeling reduces emotional whiplash, especially for ADHD brains.",
  "'Name it to tame it': when you label an emotion, your brain can start to regulate it.",
  "You don't need the perfect word. 'This feels big' or 'I'm struggling' is enough.",
  "Putting language to feelings helps move them from the emotional brain to the thinking brain.",
  "Even saying 'I notice I feel...' can help your nervous system settle."
];

// 4. SOMATIC GROUNDING - Body-based regulation cues
export const SOMATIC_INSIGHTS = [
  "Shake out your hands or legs to release stored energy. Movement helps emotions move through.",
  "Try deep belly breathing: let your stomach expand like a balloon, then slowly release.",
  "Hold something cool in your hands to help your body settle and come back to the present.",
  "Press your feet firmly into the ground. Feel the solid support beneath you.",
  "Gentle movement—rocking, swaying, stretching—can help discharge nervous system activation.",
  "Splash cold water on your face or wrists. Temperature change can quickly shift your state.",
  "Cross your arms and give yourself a gentle squeeze. Physical containment can feel soothing."
];

// 5. R.A.I.N. METHOD - Step-by-step guidance
export const RAIN_GUIDANCE = {
  recognize: [
    "Recognize what's happening. Just notice, without judgment.",
    "The first step is awareness: what emotion or sensation is present right now?",
    "Recognition isn't fixing. It's simply acknowledging what's here."
  ],
  allow: [
    "Notice this emotion as it rises, peaks, and softens. You don't need to change it.",
    "Let the feeling shift and change however it wants to. You're just observing.",
    "You don't have to do anything with this emotion. Just notice how it moves."
  ],
  investigate: [
    "Investigate gently: what might be underneath this feeling?",
    "With curiosity, not criticism: what does this feeling need?",
    "Is there something this emotion is trying to protect or communicate?"
  ],
  nonidentification: [
    "Remember: this feeling is real, but it isn't all of you.",
    "You are the one experiencing this emotion, not the emotion itself.",
    "Notice the emotion as it rises, peaks, and softens. You are more than this moment."
  ]
};

// 6. TOOL-SPECIFIC USAGE TIPS
export const TOOL_USAGE_TIPS = {
  overwhelm_navigator: [
    "This tool works best when you start with the body, then move to the emotion.",
    "You don't need to feel 100% sure about your overwhelm type. Pick what feels closest.",
    "It's okay to pause mid-sequence. Come back when you're ready."
  ],
  micro_reset: [
    "Micro-resets work by giving your nervous system a quick pattern interrupt.",
    "Even 10 seconds of grounding can shift your state. Small resets add up.",
    "You can do these anywhere: at your desk, in the bathroom, in your car."
  ],
  self_talk: [
    "Self-talk reframes help because they engage the thinking brain.",
    "You don't have to believe the reframe 100%. Just consider it as another perspective.",
    "Talking to yourself kindly is a skill. It gets easier with practice."
  ],
  coregulation: [
    "Co-regulation works because our nervous systems are designed to sync with others.",
    "Even imagining someone who loves you can activate co-regulation.",
    "If partner mode feels too vulnerable, solo mode is always here."
  ]
};

// Helper to get combined insights for specific contexts
export const getToolUsageInsight = (tool) => {
  const tips = TOOL_USAGE_TIPS[tool];
  return tips ? getRandomInsight(tips) : null;
};

// Get a random interoception or labeling insight
export const getBodyAwarenessInsight = () => {
  const combined = [...INTEROCEPTION_INSIGHTS, ...EMOTION_WHEEL_INSIGHTS];
  return getRandomInsight(combined);
};

// Get a somatic grounding insight
export const getSomaticInsight = () => {
  return getRandomInsight(SOMATIC_INSIGHTS);
};

// Get a labeling insight
export const getLabelingInsight = () => {
  return getRandomInsight(LABELING_INSIGHTS);
};

// Helper to get a random insight from an array
export const getRandomInsight = (insightArray) => {
  if (!insightArray || insightArray.length === 0) return null;
  return insightArray[Math.floor(Math.random() * insightArray.length)];
};

/**
 * MicroInsight Component
 * 
 * Props:
 * - insight: string - The insight text to display
 * - variant: 'default' | 'compact' | 'expandable' - Display style
 * - onDismiss: function - Called when user dismisses the insight
 * - className: string - Additional CSS classes
 */
export const MicroInsight = ({ 
  insight, 
  variant = 'default',
  onDismiss,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (!insight || isDismissed) return null;

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  // Compact variant - just a hint that can expand
  if (variant === 'compact') {
    return (
      <div className={`brand-fade-in ${className}`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left p-3 rounded-xl bg-[#FEF6EF]/60 hover:bg-[#FEF6EF] transition-colors border border-[#FFBB98]/50 group"
          data-testid="micro-insight-toggle"
        >
          <div className="flex items-center gap-2">
            <Lightbulb className="w-3.5 h-3.5 text-[#B8A080] flex-shrink-0" />
            <SmallText className="text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors">
              {isExpanded ? 'Hide' : 'Why does this happen?'} (optional)
            </SmallText>
            {isExpanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-[#7D8E95] ml-auto" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-[#7D8E95] ml-auto" />
            )}
          </div>
          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-[#FFBB98]/50">
              <SmallText className="text-[#7D8E95] leading-relaxed block">
                {insight}
              </SmallText>
            </div>
          )}
        </button>
      </div>
    );
  }

  // Expandable variant - shows a teaser, expands on click
  if (variant === 'expandable') {
    return (
      <div className={`brand-fade-in ${className}`}>
        <div className="p-4 rounded-2xl bg-[#FEF6EF]/50 border border-[#FFBB98]/40">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#E8DFD4] flex items-center justify-center flex-shrink-0 mt-0.5">
              <Lightbulb className="w-3.5 h-3.5 text-[#A89080]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <SmallText className="text-[#7D8E95] font-medium">
                  A gentle note about your brain
                </SmallText>
                <button
                  onClick={handleDismiss}
                  className="p-1 rounded-full hover:bg-[#FFBB98]/50 transition-colors"
                  aria-label="Dismiss insight"
                  data-testid="dismiss-insight"
                >
                  <X className="w-3.5 h-3.5 text-[#7D8E95]" />
                </button>
              </div>
              <SmallText className="text-[#7D8E95] leading-relaxed block">
                {insight}
              </SmallText>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <SmallText className="text-[#7D8E95] italic text-xs">
              Skip anytime — this is optional
            </SmallText>
          </div>
        </div>
      </div>
    );
  }

  // Default variant - simple inline display
  return (
    <div className={`brand-fade-in ${className}`} data-testid="micro-insight">
      <div className="p-4 rounded-2xl bg-[#FEF6EF]/40 border border-[#FFBB98]/30">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 rounded-full bg-[#E8DFD4]/80 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Lightbulb className="w-3 h-3 text-[#A89080]" />
          </div>
          <div className="flex-1">
            <SmallText className="text-[#7D8E95] leading-relaxed block">
              {insight}
            </SmallText>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-[#FFBB98]/50 transition-colors flex-shrink-0"
            aria-label="Dismiss insight"
            data-testid="dismiss-insight-btn"
          >
            <X className="w-3.5 h-3.5 text-[#7D8E95]" />
          </button>
        </div>
        <div className="mt-2 ml-8">
          <SmallText className="text-[#7D8E95] italic text-xs">
            Optional — skip anytime
          </SmallText>
        </div>
      </div>
    </div>
  );
};

export default MicroInsight;
